interface ErrorMessagePattern {
  pattern?: RegExp;
  message: string;
  fieldPattern?: RegExp;
}

/**
 * Error Message Mapping Configuration
 *
 * Maps technical error messages to user-friendly validation messages using regex patterns.
 * Each entry contains a regex pattern and the corresponding user-friendly message.
 */
export const ERROR_MESSAGE_PATTERNS = [
  // Contract Type Related Errors
  {
    pattern: /Required properties are missing from object: contract_type/,
    message: 'You must set a Type - Contract Classification',
  },
  {
    pattern: /Mandatory field is missing, when contract_type value has been provided/,
    message: 'You must provide a Policyholder - Name (Insured) when the Contract Classification is set',
  },

  // Reinsurance Contract Errors
  {
    pattern: /Field value 'insured' expected count of 1, when contract_type = 'reinsurance_contract'/,
    message:
      'You must provide a Original Insured - Name (Insured) when the Contract Classification is set to Reinsurance',
  },
  {
    pattern: /Contract Insured list count of 2 expected, when contract_type = 'reinsurance_contract'/,
    message:
      'You must provide a Policyholder (Reinsured) and an Original Insured (Insured) when the Contract Classification is set to Reinsurance',
  },
  {
    pattern: /Field value 'reinsured' expected count of 1, when contract_type = 'reinsurance_contract'/,
    message:
      'You must provide an Policyholder - Name (Reinsured) when the Contract Classification is set to Reinsurance',
  },

  // Retrocession Contract Errors
  {
    pattern: /Field value 'insured' expected count of 1, when contract_type = 'retrocession_contract'/,
    message: 'You must provide a Original Insured - Name (Insured) when Retrocession is set to Yes',
  },
  {
    pattern: /Field value 'reinsured' expected count of 1, when contract_type = 'retrocession_contract'/,
    message: 'You must provide a Type - Reinsured Name (Reinsured) when Retrocession is set to Yes',
  },
  {
    pattern: /Contract Insured list count of 3 expected, when contract_type = 'retrocession_contract'/,
    message:
      'You must provide a Policyholder (Retrocedent), an Original Insured (Insured) and a Reinsured (Reinsured) when Retrocession is set to Yes',
  },
  {
    pattern: /Field value 'retrocedent' expected count of 1, when contract_type = 'retrocession_contract'/,
    message: 'You must provide a Policyholder - Name (Retrocedent) when Retrocession is set to Yes',
  },

  // Period/Date Related Errors
  {
    pattern: /Required properties are missing from object: inception_date/,
    message: 'You must provide a Period - From Date',
  },
  {
    pattern: /Required properties are missing from object: expiry_date/,
    message: 'You must provide a Period - To Date',
  },
  {
    pattern: /Required properties are missing from object: expiry_date, inception_date/,
    message: 'You must provide a Period - From Date and a Period - To Date',
  },
  {
    pattern: /Field value must be on or before .*\.expiry_date/,
    message: 'Period - From Date must be after Period - To Date',
  },
  {
    pattern: /Field value must be on or after the field default_section.period.period_date.inception_date value/,
    message: 'Settlement Date must be after Period - From Date',
  },
  {
    pattern: /Field value must be on or after .*\.inception_date/,
    message: 'Period - To Date must be before Period - From Date',
  },
  {
    pattern: /Mandatory field is missing, when field .*\.qualifier_type/,
    message: 'You must set Period - Date Inclusive/Exclusive Indicator',
  },

  // Amount and Currency Related Errors
  {
    pattern: /Required properties are missing from object: amount, basis_type/,
    message: 'You must provide an Amount and Basis',
  },
  {
    pattern: /Required properties are missing from object: amount, currency_code/,
    message: 'You must provide a Currency and Amount',
  },
  {
    pattern: /Required properties are missing from object: basis_type, currency_code/,
    message: 'You must provide a Currency and Basis',
  },
  {
    pattern: /Required properties are missing from object: currency_code/,
    message: 'You must provide a Currency',
  },
  {
    pattern: /currency_code: Mandatory field is missing/,
    message: 'You must provide a Currency',
  },
  {
    pattern: /Required properties are missing from object: amount/,
    message: 'You must provide an Amount',
  },
  {
    pattern: /amount: Mandatory field is missing/,
    message: 'You must provide an Amount',
  },
  {
    pattern: /Required properties are missing from object: basis_type/,
    message: 'You must provide a Basis',
  },
  {
    pattern: /basis_description: Mandatory field is missing/,
    message: 'You must provide a Basis',
  },
  {
    pattern: /Required properties are missing from object: type/,
    message: 'You must provide a Type',
  },
  {
    pattern: /rate: Mandatory field is missing/,
    message: 'You must provide an Amount or Percentage',
  },
  {
    pattern: /.*\.rate: Mandatory field is missing/,
    message: 'You must provide an Amount or Percentage',
  },

  // Section Related Errors
  {
    pattern: /Required properties are missing from object: description, reference/,
    message: 'You must provide a Section Description and Placing Broker Section Reference',
  },
  {
    pattern: /Required properties are missing from object: reference/,
    message: 'You must provide a Section - Placing Broker Section Reference',
  },
  {
    pattern: /Required properties are missing from object: description/,
    message: 'You must provide a Section Description',
  },
  {
    pattern: /Required properties are missing from object: risk_code/,
    message: 'You must select an Allocation of Premium to Coding - Risk Code',
  },
  {
    pattern: /This Contract is currently in use for a Submission/,
    message: 'As this contract is now part of a submission within PPL, data can no longer be synced',
  },
  {
    fieldPattern: /cover_type/,
    message: 'You must provide a valid Cover Type for the Contract Classification selected',
  },
  {
    pattern: /insureds: Mandatory field is missing/,
    message: 'You must provide a Policyholder - Name (Insured) when the Contract Classification is set',
  },
  {
    pattern: /Field value 'insured' expected count of 1, when classification_type = 'retrocession_contract'/,
    message: 'You must provide a Original Insured - Name (Insured) when Retrocession is set to Yes',
  },
  {
    pattern: /Contract Insured list count of 3 expected, when classification_type = 'retrocession_contract'/,
    message:
      'You must provide a Policyholder (Retrocedent), an Original Insured (Insured) and a Reinsured (Reinsured) when Retrocession is set to Yes',
  },
  {
    pattern: /Field value 'retrocedent' expected count of 1, when classification_type = 'retrocession_contract'/,
    message: 'You must provide a Policyholder - Name (Retrocedent) when Retrocession is set to Yes',
  },
  {
    fieldPattern: /default_section.settlement_due_date/,
    message: 'Settlement Date must be after Period - From Date',
  },
  {
    pattern: /allocation_percentage: Mandatory field is missing/,
    message: 'You must provide a Risk Code Premium Allocation percentage',
  },
  {
    pattern: /allocation_percentage values has not been met/,
    message: 'You must provide 100% Risk Code Premium Allocation',
  },
  {
    fieldPattern: /sectionID/,
    message: 'This section no longer exists, please sync from PPL to update sections ',
  },
] as ErrorMessagePattern[];

/**
 * Find the matching pattern for an error message and return the user-friendly message
 */
export const getErrorMessageMapping = (message: string, field?: string): string | null => {
  for (const { pattern, message: userMessage, fieldPattern } of ERROR_MESSAGE_PATTERNS) {
    if (pattern?.test(message) || (field && fieldPattern?.test(field))) {
      return userMessage;
    }
  }
  return null;
};

/**
 * Check if a message has a known mapping
 */
export const isKnownErrorMessage = (message: string): boolean => {
  return getErrorMessageMapping(message) !== null;
};
